import { useCallback, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Trans } from "react-i18next"

import _ from "lodash"

import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slide,
  Zoom,
  Button,
  Fab,
  useTheme,
  Paper,
  Avatar,
  CardMedia,
} from "@mui/material"

import { getIcon } from "utils/icon"
import { isValidURL } from "utils/url"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"

import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"

import {
  BackIcon,
  PrevStepIcon,
  NextStepIcon,
  MarkerIcon,
  OriginMarkerIcon,
} from "components/icons"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { OrdinalSelector } from "components/OrdinalSelector"
import { ExitIcon } from "components/icons"

export const StepByStepKioskVPage = () => {
  const navigate = useNavigate()
  const [animateScroll, setAnimateScroll] = useState(false)
  const { dataLoaded, isMultiOrdinalVenue, isMultiVenueProject } = useVenue()

  const {
    steps,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    currentLevel,
    currentOrdinal,
    originName,
    originLogo,
    originVenueLogo,
    originCategoryIconPath,
    originVenueData,
    destinationName,
    destinationVenueLogo,
    destinationVenueData,
    route,
    hasNextStep,
    hasPrevStep,
    currentVerticalStepMethod,
    exitPath,
    directionOverviewPath,
    goToStepByIndex,
    goToPrevStep,
    goToNextStep,
  } = useStepByStep()

  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: 411 },
    enabled: dataLoaded && steps.length > 0,
  })

  const { t } = useTranslation()

  const theme = useTheme()

  const {
    container,
    primaryText,
    title,
    locationText,
    originIcon,
    destinationIcon,
    navButtonWrapper,
    headerWrapper,
    contentWrapper,
    navButton,
    iconNavButton,
    ordinalSelectorWrapper,
    wrapper,
    kioskBackButton,
    highlightedText,
    stepTextContainer,
    exitButtonWrapper,
    exitButton,
    progressBar,
    stepProgressBar,
    originIconContainer,
    destinationIconContainer,
    originAvatar,
    actionButtonWrapper,
    stepProgressBarContainer,
    destinationAvatar,
    venueLogo,
    venueLogoContainer,
    locationTextContainer,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  const exitIcon = useMemo(
    () => getIcon(theme?.assets?.exitIcon) || <ExitIcon />,
    [theme]
  )

  const originIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <MarkerIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const originCategoryIcon = useMemo(
    () => getIcon(originCategoryIconPath),
    [originCategoryIconPath]
  )

  const prevStepIcon = useMemo(
    () => getIcon(theme?.assets?.prevIcon) || <PrevStepIcon />,
    [theme]
  )

  const nextStepIcon = useMemo(
    () => getIcon(theme?.assets?.nextIcon) || <NextStepIcon />,
    [theme]
  )

  const handleEnteredSlide = useCallback(() => {
    setAnimateScroll(true)
  }, [])

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const originVenueRef = _.get(originVenueData, "reference")
  const destinationVenueRef = _.get(destinationVenueData, "reference")

  const handleClickExit = useCallback(() => {
    navigate(directionOverviewPath, {
      replace: true,
    })
  }, [navigate, directionOverviewPath])
  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
      <Slide
        direction="up"
        in={dataLoaded && steps.length > 0}
        onEntered={handleEnteredSlide}
        mountOnEnter
        unmountOnExit
      >
        <Box sx={wrapper}>
          {/* only show ordinalSelector when there is no VerticalDirectionImageDialog showing */}
          {isMultiOrdinalVenue && !showVerticalStepDialog && (
            <Box sx={ordinalSelectorWrapper}>
              <OrdinalSelector
                levels={currentLevel}
                selectedOrdinal={currentOrdinal}
              />
            </Box>
          )}
          <Box sx={actionButtonWrapper}>
            <Fab
              variant="rounded"
              onClick={handleClickExit}
              sx={kioskBackButton}
            >
              {backIcon}
            </Fab>
            <Box sx={exitButtonWrapper}>
              <Button
                component={Link}
                to={exitPath}
                startIcon={exitIcon}
                sx={exitButton}
                variant="contained"
              >
                {t("Exit")}
              </Button>
            </Box>
          </Box>
          <Paper sx={container}>
            <Stack
              width="100%"
              spacing={0}
              direction="column"
              height="100%"
              justifyContent="space-between"
            >
              <Box sx={contentWrapper}>
                <Box sx={headerWrapper}>
                  <Stack alignItems="center" spacing={1}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Zoom in={hasPrevStep}>
                        <IconButton
                          color="inherit"
                          sx={iconNavButton}
                          onClick={goToPrevStep}
                        >
                          <PrevStepIcon fontSize="inherit" />
                        </IconButton>
                      </Zoom>

                      <Typography
                        component={"h1"}
                        textAlign="center"
                        sx={title}
                      >
                        {t("Process step", {
                          currentStep: currentStepNumber,
                          totalSteps: steps.length,
                        })}
                      </Typography>

                      <Zoom in={hasNextStep}>
                        <IconButton
                          color="inherit"
                          sx={iconNavButton}
                          onClick={goToNextStep}
                        >
                          <NextStepIcon fontSize="inherit" />
                        </IconButton>
                      </Zoom>
                    </Stack>
                    <Box sx={stepTextContainer}>
                      <Typography
                        variant="subTitle"
                        component="p"
                        sx={primaryText}
                      >
                        <Trans
                          components={{
                            category: highlightedStepText,
                            destination: highlightedStepText,
                            landmark: highlightedStepText,
                            intermediary: highlightedStepText,
                            toward: highlightedStepText,
                            turn: highlightedStepText,
                          }}
                        >
                          {t(currentStep?.description.text, {
                            category: t(
                              currentStep?.description.options?.category
                            ),
                            destination: t(
                              currentStep?.description.options?.destination
                            ),
                            landmark: t(
                              currentStep?.description.options?.landmark
                            ),
                            intermediary: t(
                              currentStep?.description.options?.intermediary
                            ),
                            toward: t(currentStep?.description.options?.toward),
                            turn: t(currentStep?.description.options?.turn),
                          })}
                        </Trans>
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                {/* kiosk-v progress bar */}
                <Stack
                  width="100%"
                  height="100%"
                  direction="row"
                  alignItems={"flex-end"}
                  sx={progressBar}
                >
                  <Box sx={originIconContainer}>
                    <Avatar sx={originAvatar} src={originLogo}>
                      {originCategoryIcon ? originCategoryIcon : originIconComp}
                    </Avatar>
                    {isMultiVenueProject && originVenueLogo && (
                      <Box sx={venueLogoContainer} className={originVenueRef}>
                        <CardMedia image={originVenueLogo} sx={venueLogo} />
                      </Box>
                    )}
                    <Box sx={locationTextContainer}>
                      <Typography
                        textAlign="start"
                        variant="caption"
                        sx={locationText}
                      >
                        {t(originName)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={stepProgressBarContainer}>
                    <StepProgressBar
                      steps={steps}
                      currentStep={currentStepNumber}
                      sx={stepProgressBar}
                      handleClickStep={goToStepByIndex}
                      enableAnimateScroll={animateScroll}
                    />
                  </Box>
                  <Box sx={destinationIconContainer}>
                    <Avatar sx={destinationAvatar}>
                      {destinationIconComp}
                    </Avatar>
                    {isMultiVenueProject && destinationVenueLogo && (
                      <Box
                        sx={venueLogoContainer}
                        className={destinationVenueRef}
                      >
                        <CardMedia
                          image={destinationVenueLogo}
                          sx={venueLogo}
                        />
                      </Box>
                    )}
                    <Box sx={locationTextContainer}>
                      <Typography variant="caption" sx={locationText}>
                        {t(destinationName)}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                {/* end of kiosk-v progress bar */}
              </Box>
              <Stack sx={navButtonWrapper}>
                <Button
                  variant="outlined"
                  startIcon={prevStepIcon}
                  disabled={!hasPrevStep}
                  sx={navButton}
                  onClick={goToPrevStep}
                >
                  {t("Previous Step")}
                </Button>
                <Button
                  variant="contained"
                  disabled={!hasNextStep}
                  sx={navButton}
                  endIcon={nextStepIcon}
                  onClick={goToNextStep}
                >
                  {t("Next Step")}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Slide>
    </>
  )
}
